/**
 * Typed client for /cap-table/* — backs the Cap Table screen.
 *
 * Cap-table data is sensitive: per the cap-table-source memory note,
 * the dashboard is the single source of truth. The same backend powers
 * both surfaces; this file just types it for admin.nurev.io.
 *
 * Five resource families exposed here:
 *   1. summary       — top-line KPIs (share totals, allocated, portal users)
 *   2. config        — share class totals (class A + class B)
 *   3. shareholders  — paginated CRUD + invite
 *   4. transactions  — append-only audit log of share changes
 *   5. documents     — shared file links (legal docs, board reports)
 *   6. announcements — broadcast messages to all shareholders with
 *                      optional email notification on create
 */
import { client, unwrap } from './client';

export type InviteStatus = 'pending' | 'sent' | 'accepted';
export type AnnouncementCategory =
  | 'milestone'
  | 'product'
  | 'fundraising'
  | 'team'
  | 'general';
export type ShareClass = 'A' | 'B';

export interface ShareClassSummary {
  total: number;
  allocated: number;
  unallocated: number;
}

export interface CapTableSummary {
  totalShareholders: number;
  totalShares: number;
  totalAllocated: number;
  activePortalUsers: number;
  invitedCount: number;
  classA: ShareClassSummary;
  classB: ShareClassSummary;
}

export interface ShareConfig {
  classATotalShares: number;
  classBTotalShares: number;
  // Server may include other config fields; we type only what's used.
}

export interface Shareholder {
  _id: string;
  name: string;
  email: string;
  classAShares: number;
  classBShares: number;
  totalShares: number;
  fullyDilutedPercent?: number;
  inviteStatus: InviteStatus;
  isResidual?: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ShareholderListResponse {
  docs: Shareholder[];
  totalDocs: number;
  totalPages: number;
  page: number;
  limit: number;
}

export interface ShareTransaction {
  _id: string;
  shareholderId: string;
  shareholderName: string;
  shareClass: ShareClass;
  change: number;
  newShares: number;
  reason?: string;
  createdAt: string;
}

export interface ShareTransactionListResponse {
  docs: ShareTransaction[];
  totalDocs: number;
  totalPages: number;
  page: number;
  limit: number;
}

export interface ShareholderDocument {
  _id: string;
  title: string;
  description?: string;
  fileUrl: string;
  fileName: string;
  createdAt: string;
}

export interface DocumentListResponse {
  docs: ShareholderDocument[];
  totalDocs: number;
  totalPages: number;
  page: number;
  limit: number;
}

export interface Announcement {
  _id: string;
  title: string;
  body: string;
  category: AnnouncementCategory;
  pinned: boolean;
  publishedAt: string;
  createdAt: string;
}

export interface AnnouncementListResponse {
  docs: Announcement[];
  totalDocs: number;
  totalPages: number;
  page: number;
  limit: number;
}

/**
 * Create-announcement response shape — the server reports email
 * delivery counts when notifyByEmail is true.
 */
export interface CreateAnnouncementResponse extends Announcement {
  notify?: {
    sent: boolean;
    recipients?: number;
    delivered?: number;
    failed?: string[];
  };
}

export interface InviteShareholderResponse {
  success?: boolean;
  message?: string;
  /**
   * The accept-invite link. The API returns this so an admin can share it
   * manually if the email doesn't land. (The API does NOT return a
   * tempPassword — an earlier field by that name never existed server-side,
   * which is why invite success used to be silent.)
   */
  inviteUrl?: string;
}

// ─── Inputs ───────────────────────────────────────────────────────────────

export interface CreateShareholderInput {
  name: string;
  email: string;
  classAShares?: number;
  classBShares?: number;
  notes?: string;
}

export interface UpdateShareholderInput extends CreateShareholderInput {
  id: string;
  reason?: string;
}

export interface UpdateShareConfigInput {
  classATotalShares?: number;
  classBTotalShares?: number;
}

export interface CreateDocumentInput {
  title: string;
  description?: string;
  fileUrl: string;
  fileName: string;
}

export interface CreateAnnouncementInput {
  title: string;
  body: string;
  category: AnnouncementCategory;
  pinned?: boolean;
  notifyByEmail?: boolean;
}

export interface UpdateAnnouncementInput {
  id: string;
  title?: string;
  body?: string;
  category?: AnnouncementCategory;
  pinned?: boolean;
}

// ─── Fetchers ─────────────────────────────────────────────────────────────

export async function fetchCapTableSummary(): Promise<CapTableSummary> {
  return unwrap<CapTableSummary>(client.get('/cap-table/summary'));
}

export async function fetchShareConfig(): Promise<ShareConfig> {
  return unwrap<ShareConfig>(client.get('/cap-table/config'));
}

export async function updateShareConfig(
  input: UpdateShareConfigInput
): Promise<ShareConfig> {
  return unwrap<ShareConfig>(client.patch('/cap-table/config', input));
}

export async function fetchShareholders(params: {
  page?: number;
  limit?: number;
}): Promise<ShareholderListResponse> {
  return unwrap<ShareholderListResponse>(client.get(
    '/cap-table/shareholders',
    { params }
  ));
}

export async function createShareholder(
  input: CreateShareholderInput
): Promise<Shareholder> {
  return unwrap<Shareholder>(client.post('/cap-table/shareholders', input));
}

export async function updateShareholder(
  input: UpdateShareholderInput
): Promise<Shareholder> {
  const { id, ...body } = input;
  return unwrap<Shareholder>(client.patch(
    `/cap-table/shareholders/${id}`,
    body
  ));
}

export async function deleteShareholder(id: string): Promise<{ success: boolean }> {
  return unwrap<{ success: boolean }>(client.delete(
    `/cap-table/shareholders/${id}`
  ));
}

export async function inviteShareholder(
  id: string
): Promise<InviteShareholderResponse> {
  return unwrap<InviteShareholderResponse>(client.post(
    `/cap-table/shareholders/${id}/invite`
  ));
}

export async function fetchShareTransactions(params: {
  page?: number;
  limit?: number;
}): Promise<ShareTransactionListResponse> {
  return unwrap<ShareTransactionListResponse>(client.get(
    '/cap-table/transactions',
    { params }
  ));
}

export async function fetchDocuments(params: {
  page?: number;
  limit?: number;
}): Promise<DocumentListResponse> {
  return unwrap<DocumentListResponse>(client.get('/cap-table/documents', {
    params
  }));
}

export async function createDocument(
  input: CreateDocumentInput
): Promise<ShareholderDocument> {
  return unwrap<ShareholderDocument>(client.post(
    '/cap-table/documents',
    input
  ));
}

export async function deleteDocument(id: string): Promise<{ success: boolean }> {
  return unwrap<{ success: boolean }>(client.delete(
    `/cap-table/documents/${id}`
  ));
}

export async function fetchAnnouncements(params: {
  page?: number;
  limit?: number;
}): Promise<AnnouncementListResponse> {
  return unwrap<AnnouncementListResponse>(client.get(
    '/cap-table/announcements',
    { params }
  ));
}

export async function createAnnouncement(
  input: CreateAnnouncementInput
): Promise<CreateAnnouncementResponse> {
  return unwrap<CreateAnnouncementResponse>(client.post(
    '/cap-table/announcements',
    input
  ));
}

export async function updateAnnouncement(
  input: UpdateAnnouncementInput
): Promise<Announcement> {
  const { id, ...body } = input;
  return unwrap<Announcement>(client.patch(
    `/cap-table/announcements/${id}`,
    body
  ));
}

export async function deleteAnnouncement(
  id: string
): Promise<{ success: boolean }> {
  return unwrap<{ success: boolean }>(client.delete(
    `/cap-table/announcements/${id}`
  ));
}
